import { Button } from "@promptwatch/ui/components/button";
import { Input } from "@promptwatch/ui/components/input";
import { ArrowRight, BookOpen, Search } from "lucide-react";
import type { GetStaticProps } from "next";
import { useMemo, useState } from "react";
import BaseLayout from "@/components/base-layout";
import { Header } from "@/components/header";
import { TermCard } from "@/components/term-card";
import { type GlossaryTerm, glossaryCategories, glossaryTerms, searchTerms } from "@/data/glossary";
import { clientEnv } from "../../env/client";

interface GlossaryIndexProps {
  terms: GlossaryTerm[];
}

export default function GlossaryIndex({ terms }: GlossaryIndexProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const filteredTerms = useMemo(() => {
    let filtered = terms || [];

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = searchTerms(searchQuery, terms);
    }

    // Apply category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter((term) => term.category === selectedCategory);
    }

    // Sort alphabetically
    return filtered.sort((a, b) => a.title.localeCompare(b.title));
  }, [terms, searchQuery, selectedCategory]);

  const termsByCategory = useMemo(() => {
    const safeTerms = terms || [];
    const grouped = glossaryCategories.reduce(
      (acc, category) => {
        acc[category.value] = safeTerms.filter((term) => term.category === category.value);
        return acc;
      },
      {} as Record<string, GlossaryTerm[]>,
    );

    // Sort terms within each category
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.title.localeCompare(b.title));
    });

    return grouped;
  }, [terms]);

  const safeTerms = terms || [];

  // Explicitly determine which view to show
  const hasActiveFilters = Boolean(searchQuery.trim()) || selectedCategory !== "all";
  const hasResults = filteredTerms.length > 0;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "AI SEO & GEO Glossary",
    description:
      "Comprehensive glossary of AI-powered search, SEO, and Generative Engine Optimization terms. Stay ahead of the curve with definitions, examples, and insights into the future of search optimization.",
    url: "https://promptwatch.com/glossary",
    dateModified: new Date().toISOString(),
    publisher: {
      "@type": "Organization",
      name: "Promptwatch",
      url: "https://promptwatch.com",
      logo: "https://promptwatch.com/favicon.svg",
    },
    hasDefinedTerm: safeTerms.map((term) => ({
      "@type": "DefinedTerm",
      name: term.title,
      description: term.definition,
      identifier: term.slug,
      url: `https://promptwatch.com/glossary/${term.slug}`,
      dateModified: term.lastUpdated,
      about: {
        "@type": "Thing",
        name:
          term.category === "AI"
            ? "Artificial Intelligence"
            : term.category === "SEO"
              ? "Search Engine Optimization"
              : term.category === "GEO"
                ? "Generative Engine Optimization"
                : term.category,
      },
    })),
    numberOfItems: safeTerms.length,
  };

  // Render functions for different views
  const renderNoResultsView = () => (
    <div className="text-center py-12">
      <p className="text-gray-500 text-lg">No terms found matching your criteria.</p>
      <Button
        variant="outline"
        onClick={() => {
          setSearchQuery("");
          setSelectedCategory("all");
        }}
        className="mt-4"
      >
        Clear Filters
      </Button>
    </div>
  );

  const renderFilteredResultsView = () => (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {filteredTerms.map((term) => (
        <TermCard key={`filtered-${term.slug}`} term={term} />
      ))}
    </div>
  );

  const renderCategoryView = () => (
    <div className="space-y-12">
      {glossaryCategories.map((category) => {
        const categoryTerms = termsByCategory[category.value];
        if (!categoryTerms || categoryTerms.length === 0) return null;

        return (
          <div key={`category-${category.value}`}>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{category.label}</h2>
              <div className="h-1 w-16 bg-primary rounded" />
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {categoryTerms.map((term) => (
                <TermCard key={`category-${category.value}-${term.slug}`} term={term} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <BaseLayout>
      <Header
        title="AI SEO & GEO Glossary | Promptwatch.com"
        description="Your comprehensive guide to AI-powered search terminology. Stay ahead of the curve with definitions, examples, and insights into AI SEO, GEO, and the future of search optimization."
        canonical="https://promptwatch.com/glossary"
        ogType="website"
        author={{
          name: "Promptwatch Team",
          url: "https://promptwatch.com",
          sameAs: ["https://twitter.com/promptwatchcom", "https://www.linkedin.com/company/promptwatch"],
        }}
        tags={["AI Search", "SEO", "GEO", "Glossary", "AI Terms", "Search Engine Optimization", "Generative Engine Optimization"]}
        structuredData={structuredData}
        metaImage="https://promptwatch.com/meta.png"
      />
      <main className="duration-150">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
        <div
          aria-hidden="true"
          className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
        />

        {/* Hero Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07] px-4 pb-16 pt-16 sm:px-12">
              <div className="relative w-fit bg-gray-950/5 p-2">
                <div aria-hidden="true" className="absolute left-1 top-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute right-1 top-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute bottom-1 left-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute bottom-1 right-1 size-[3px] rounded-full bg-gray-950/20" />
                <div className="relative flex h-fit items-center gap-2 rounded-full bg-white px-3 py-1 shadow">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <span className="text-title text-sm flex items-center gap-2 px-1">AI SEO & GEO Glossary</span>
                </div>
              </div>
              <h1 className="text-title mt-6 text-4xl font-medium max-w-4xl sm:mt-12 sm:text-5xl lg:text-5xl">
                AI SEO & GEO Glossary: <br /> Essential Terms for <span className="text-muted-foreground">Search Marketers</span>
              </h1>
              <p className="text-lg pt-3 max-w-3xl text-muted-foreground">
                Expert definitions, real-world examples, and practical insights for SEO professionals, marketers, and business owners
                working with AI-powered search optimization.
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -top-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="bg-white/50 relative border-x border-gray-950/[0.07] px-4 py-8 sm:px-12">
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search glossary terms..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12 text-lg bg-white"
                  />
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap gap-2">
                  <Button variant={selectedCategory === "all" ? "default" : "outline"} size="xs" onClick={() => setSelectedCategory("all")}>
                    All Categories
                  </Button>
                  {glossaryCategories
                    .filter((category) => termsByCategory[category.value]?.length > 0)
                    .map((category) => (
                      <Button
                        key={category.value}
                        variant={selectedCategory === category.value ? "default" : "outline"}
                        size="xs"
                        onClick={() => setSelectedCategory(category.value)}
                      >
                        {category.label}
                      </Button>
                    ))}
                </div>

                {/* Results Count */}
                {hasActiveFilters && (
                  <p className="text-sm text-gray-600">
                    {filteredTerms.length} {filteredTerms.length === 1 ? "term" : "terms"} found
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Terms Display */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -top-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -top-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="bg-white/50 relative border-x border-gray-950/[0.07] px-4 py-12 sm:px-12">
              <div className="space-y-6">
                {hasActiveFilters
                  ? // Show filtered results or no results message
                    hasResults
                    ? renderFilteredResultsView()
                    : renderNoResultsView()
                  : // Show category-based view
                    renderCategoryView()}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="px-5 sm:px-20 border-x border-gray-950/[0.07] hover:!bg-ui relative p-8 shadow-lg shadow-gray-900/5 before:absolute before:inset-1 before:rounded-b-lg before:bg-gradient-to-t before:from-blue-200 group-hover:bg-transparent">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-6 md:mb-0 z-40">
                  <h3 className="text-lg font-semibold mb-2">Stay Ahead of AI Search Evolution</h3>
                  <p className="text-gray-800/80 max-w-lg text-sm">
                    The world of AI-powered search is rapidly evolving. Get your business ready for the future of search with our monitoring
                    and optimization platform.
                  </p>
                </div>
                <div className="flex flex-row gap-4 z-40">
                  <Button variant="outline" size="sm" href="/">
                    Learn More About GEO
                  </Button>
                  <Button size="sm" className="flex items-center gap-2" href={clientEnv.NEXT_PUBLIC_APP_URL}>
                    Start Free Trial <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </BaseLayout>
  );
}

export const getStaticProps: GetStaticProps<GlossaryIndexProps> = async () => {
  return {
    props: {
      terms: glossaryTerms,
    },
  };
};
